import { PaymentMethod } from '@repo/common';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    cn,
} from '@repo/shadcn';
import { X } from 'lucide-react';
import { ReactNode } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { SelectField, TextField } from '@/components/form/fields';
import { formatCurrencyInBDT } from '@/utils';

import { TAddPaymentForm } from './add-payment-form.schema';

type AddPaymentProps = {
    totalPrice: number;
    children?: ReactNode;
    multiplePayments?: boolean;
};

export const AddPayment = ({
    totalPrice,
    children,
    multiplePayments = true,
}: AddPaymentProps) => {
    const { watch } = useFormContext<TAddPaymentForm>();

    const payments = watch('payments');

    const currentPaidAmount =
        payments?.reduce((acc, value) => acc + Number(value?.amount ?? 0), 0) ??
        0;

    const dueAmount = totalPrice - currentPaidAmount;

    const renderDueAmount = () => {
        if (dueAmount > 0) {
            return (
                <span className={cn('text-lg font-medium', 'text-red-600')}>
                    Due: {formatCurrencyInBDT(dueAmount)}
                </span>
            );
        }

        if (dueAmount === 0) {
            return (
                <span className={cn('text-lg font-medium', 'text-green-600')}>
                    Due: {formatCurrencyInBDT(dueAmount)}
                </span>
            );
        }

        if (dueAmount < 0) {
            return (
                <span className={cn('text-lg font-medium', 'text-green-600')}>
                    Return: {formatCurrencyInBDT(Math.abs(dueAmount))}
                </span>
            );
        }

        return (
            <span className={cn('text-lg font-medium')}>
                Due: {formatCurrencyInBDT(dueAmount)}
            </span>
        );
    };

    return (
        <Card>
            <CardHeader className="px-3 py-3">
                <CardTitle className="flex justify-between">
                    <span>Payment</span>

                    {renderDueAmount()}
                </CardTitle>
            </CardHeader>
            <CardContent className="px-3 py-3">
                <div className="mb-4 space-y-2 last:mb-0">
                    {payments.map((_, index) => (
                        <AddPaymentEntryForm index={index} key={index} />
                    ))}
                </div>

                {multiplePayments && <AddNewPayment />}

                {children}
            </CardContent>
        </Card>
    );
};

AddPayment.displayName = 'AddPayment';

type AddPaymentEntryFormProps = {
    index: number;
};

const AddPaymentEntryForm = ({ index }: AddPaymentEntryFormProps) => {
    const { control, watch } = useFormContext<TAddPaymentForm>();

    const payments = watch('payments');

    const { remove } = useFieldArray({
        control,
        name: 'payments',
    });

    const renderRemoveButton = () => {
        if (payments.length <= 1 || index === 0) return null;

        return (
            <Button
                type="button"
                variant="link"
                size="icon"
                className="absolute right-0.5 top-0.5"
                onClick={handleRemovePayment}
            >
                <X className="size-4" />
            </Button>
        );
    };

    const handleRemovePayment = () => {
        if (payments.length <= 1) return;
        remove(index);
    };

    return (
        <div className="relative rounded-md border border-black/30 p-1">
            {renderRemoveButton()}

            <div className="space-y-1">
                <TextField<TAddPaymentForm>
                    name={`payments.${index}.amount`}
                    label="Amount"
                    placeholder="Enter amount"
                    className="make-payment-input ring-offset-2 focus-visible:ring-1"
                    inputClass="text-right"
                />
            </div>

            <div className="mt-4 flex items-start gap-4">
                <SelectField<TAddPaymentForm>
                    name={`payments.${index}.payment_method`}
                    label="Payment Method"
                    options={[
                        {
                            label: 'Cash',
                            value: PaymentMethod.CASH,
                        },
                        {
                            label: 'Bank',
                            value: PaymentMethod.BANK,
                        },
                        {
                            label: 'Bkash',
                            value: PaymentMethod.MobileBankingBkash,
                        },
                        {
                            label: 'Nagad',
                            value: PaymentMethod.MobileBankingNagad,
                        },
                        {
                            label: 'Rocket',
                            value: PaymentMethod.MobileBankingRocket,
                        },
                    ]}
                />
                {payments[index].payment_method !== PaymentMethod.CASH && (
                    <TextField<TAddPaymentForm>
                        name={`payments.${index}.transaction_id`}
                        placeholder="Transaction ID"
                        label="Transaction ID"
                        className="w-full"
                    />
                )}
            </div>
        </div>
    );
};

const AddNewPayment = () => {
    const { control } = useFormContext<TAddPaymentForm>();

    const { append } = useFieldArray({
        control,
        name: 'payments',
    });

    const handleAddNewPayment = () => {
        append({
            // @ts-expect-error initial value
            amount: undefined,
            payment_method: PaymentMethod.CASH,
            transaction_id: '',
        });
    };

    return (
        <div className="flex justify-end">
            <Button
                type="button"
                variant="link"
                size="sm"
                onClick={handleAddNewPayment}
            >
                Add Payment
            </Button>
        </div>
    );
};
